import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Play, ArrowLeft, Gamepad2, Target, Star, Zap, Lock } from 'lucide-react'
import { useEffect, useState } from 'react'
import { auth } from '@/config/firebase'
import { getUserSubscription, isPremiumSubscription } from '@/services/subscriptionService'

export default function PearlPopInfoPage() {
  const [isPremium, setIsPremium] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkAccess = async () => {
      const user = auth.currentUser
      if (user) {
        const subscription = await getUserSubscription(user.uid)
        setIsPremium(isPremiumSubscription(subscription))
      }
      setLoading(false)
    }
    checkAccess()
  }, [])

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <Link to="/" className="inline-flex items-center text-dark-navy/70 hover:text-dark-navy mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Games
      </Link>

      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-block relative">
          <div className="text-6xl mb-4">🦪</div>
          {!loading && !isPremium && (
            <div className="absolute -top-2 -right-8 bg-medium-purple text-white text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Premium
            </div>
          )}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-dark-navy mb-4">Pearl Pop</h1>
        <p className="text-xl text-dark-navy/70 max-w-2xl mx-auto">
          A colourful bubble shooter puzzle game! Aim carefully and match pearls of the same colour 
          to pop them before they reach the danger line.
        </p>
        
        {/* Play/Unlock Button */}
        <div className="mt-8">
          {loading ? (
            <div className="inline-flex items-center justify-center px-8 py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-medium-purple border-t-transparent"></div>
            </div>
          ) : isPremium ? (
            <Link to="/pearl-pop/play">
              <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-bright-coral hover:bg-sandy-coral text-white">
                <Play className="w-5 h-5 mr-2" />
                Start Playing
              </Button>
            </Link>
          ) : (
            <Link to="/subscription">
              <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-purple hover:bg-deep-purple text-white">
                <Lock className="w-5 h-5 mr-2" />
                Unlock Premium
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Screenshot Section */}
      <div className="mb-12">
        <div className="bg-gradient-to-br from-medium-purple/80 to-deep-purple/90 rounded-2xl overflow-hidden shadow-xl aspect-[6/7] max-w-md mx-auto flex items-center justify-center">
          <img 
            src="/screenshots/pearl_pop.jpg" 
            alt="Pearl Pop gameplay screenshot"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
              e.currentTarget.nextElementSibling?.classList.remove('hidden')
            }}
          />
          <div className="hidden text-center p-8">
            <Gamepad2 className="w-16 h-16 text-white/50 mx-auto mb-4" />
            <p className="text-white/70">Screenshot coming soon!</p>
          </div>
        </div>
      </div>

      {/* Quick Start Guide */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Target className="w-6 h-6 text-bright-coral" />
          How to Play
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🎯</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Aim</h3>
              <p className="text-dark-navy/70 text-sm">Drag back on the pearl to aim your shot</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🚀</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Shoot</h3>
              <p className="text-dark-navy/70 text-sm">Release to launch your pearl towards the target</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">💎</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Match 3+</h3>
              <p className="text-dark-navy/70 text-sm">Connect 3 or more same-coloured pearls to pop them</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">⚠️</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Watch the Line</h3>
              <p className="text-dark-navy/70 text-sm">Don't let pearls reach the danger line at the bottom!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Strategy Guide */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Star className="w-6 h-6 text-medium-teal" />
          Strategy Tips
        </h2>
        
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-dark-navy text-lg mb-3">Scoring Bonuses</h3>
            <ul className="space-y-2 text-dark-navy/80">
              <li className="flex items-center gap-2">
                <span className="text-xl">🌊</span>
                <span><strong>Chain Reactions</strong> – Disconnected pearls fall for extra points!</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-xl">💫</span>
                <span><strong>Large Matches</strong> – Match more than 3 for bonus pearls.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-xl">🏆</span>
                <span><strong>Clear the Board</strong> – Pop all pearls to complete the level!</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold text-dark-navy text-lg mb-3">Pro Tips</h3>
            <ul className="space-y-2 text-dark-navy/80">
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-bright-coral" />
                <span>Aim for clusters to create chain reactions!</span>
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-bright-coral" />
                <span>Use the walls to bounce shots into tricky spots.</span>
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-bright-coral" />
                <span>Look ahead at your next pearl to plan your strategy.</span>
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-bright-coral" />
                <span>Sometimes it's better to add to a group than pop a small match.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Pearl Colours */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6">Pearl Colours</h2>
        
        <div className="flex flex-wrap gap-4 justify-center">
          <div className="w-14 h-14 bg-gradient-to-br from-red-400 to-red-600 rounded-full shadow-lg flex items-center justify-center">
            <div className="w-4 h-4 bg-white/40 rounded-full"></div>
          </div>
          <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full shadow-lg flex items-center justify-center">
            <div className="w-4 h-4 bg-white/40 rounded-full"></div>
          </div>
          <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-full shadow-lg flex items-center justify-center">
            <div className="w-4 h-4 bg-white/40 rounded-full"></div>
          </div>
          <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full shadow-lg flex items-center justify-center">
            <div className="w-4 h-4 bg-white/40 rounded-full"></div>
          </div>
          <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full shadow-lg flex items-center justify-center">
            <div className="w-4 h-4 bg-white/40 rounded-full"></div>
          </div>
          <div className="w-14 h-14 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full shadow-lg flex items-center justify-center">
            <div className="w-4 h-4 bg-white/40 rounded-full"></div>
          </div>
        </div>
        <p className="text-center text-dark-navy/60 mt-4 text-sm">
          Match any 3 or more pearls of the same colour to pop them!
        </p>
      </div>

      {/* Difficulty Levels */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6">Difficulty Levels</h2>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-100/50 rounded-xl border-2 border-green-200">
            <h3 className="font-bold text-green-700 mb-2">🌱 Easy</h3>
            <p className="text-sm text-dark-navy/70">Fewer colours, slower pace. Great for learning the mechanics!</p>
          </div>
          
          <div className="p-4 bg-yellow-100/50 rounded-xl border-2 border-yellow-200">
            <h3 className="font-bold text-yellow-700 mb-2">🌿 Medium</h3>
            <p className="text-sm text-dark-navy/70">More colours and faster gameplay. A balanced challenge!</p>
          </div>
          
          <div className="p-4 bg-red-100/50 rounded-xl border-2 border-red-200">
            <h3 className="font-bold text-red-700 mb-2">🔥 Hard</h3>
            <p className="text-sm text-dark-navy/70">All colours, fast pace. For puzzle masters only!</p>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="text-center py-8">
        {isPremium ? (
          <Link to="/pearl-pop/play">
            <Button size="lg" className="text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-bright-coral hover:bg-sandy-coral text-white">
              <Play className="w-5 h-5 mr-2" />
              Start Popping Pearls!
            </Button>
          </Link>
        ) : (
          <div className="space-y-4">
            <Link to="/subscription">
              <Button size="lg" className="text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-purple hover:bg-deep-purple text-white">
                <Lock className="w-5 h-5 mr-2" />
                Unlock Premium to Play
              </Button>
            </Link>
            <p className="text-dark-navy/60 text-sm">
              Premium includes all games, character creator, and supports ocean conservation!
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
